import { Controller } from '@hotwired/stimulus'

interface ModalController {
  open: () => void
  close: () => void
}

export default class extends Controller {
  static targets = ['input']
  static values = {
    requiresConfirmation: Boolean,
    modalId: String
  }

  declare readonly inputTargets: HTMLInputElement[]
  declare readonly requiresConfirmationValue: boolean
  declare readonly modalIdValue: string

  private currentValue?: string
  private pendingInput?: HTMLInputElement | null

  connect (): void {
    this.currentValue = this.selectedValue()
  }

  handleChange (event: Event): void {
    const input = event.target as HTMLInputElement | null
    if (input == null || input.type !== 'radio') return

    if (!this.requiresConfirmationValue || input.value === this.currentValue) {
      this.currentValue = input.value
      this.submitForm()
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()

    this.pendingInput = input
    this.restoreSelection()
    this.openModal()
  }

  confirm (): void {
    if (this.pendingInput == null) {
      this.closeModal()
      return
    }

    this.pendingInput.checked = true
    this.currentValue = this.pendingInput.value
    this.pendingInput = null
    this.closeModal()
    this.submitForm()
  }

  cancel (): void {
    if (this.pendingInput != null) {
      this.pendingInput.checked = false
    }

    this.restoreSelection()
    this.pendingInput = null
    this.closeModal()
  }

  private restoreSelection (): void {
    if (this.currentValue === undefined) return

    const activeInput = this.inputTargets.find((target) => target.value === this.currentValue)
    if (activeInput != null) {
      activeInput.checked = true
    }
  }

  private selectedValue (): string | undefined {
    return this.inputTargets.find((target) => target.checked)?.value
  }

  private submitForm (): void {
    const form = this.element.closest('form')
    form?.requestSubmit()
  }

  private get modalController (): ModalController | null {
    const modalElement = document.getElementById(this.modalIdValue)
    if (modalElement == null) return null

    return this.application.getControllerForElementAndIdentifier(modalElement, 'modal') as ModalController | null
  }

  private openModal (): void {
    this.modalController?.open()
  }

  private closeModal (): void {
    this.modalController?.close()
  }
}
